import type { Reporter } from './types';

const LIST_KEY = 'hoppinger-feedback:reporters';
/** Before several identities were remembered, there was one. Read once and carried over. */
const LEGACY_KEY = 'hoppinger-feedback:reporter';
const MAX_REMEMBERED = 5;

const valid = (v: Partial<Reporter> | null | undefined): v is Reporter =>
  Boolean(v?.name && v?.email);

/**
 * Identities used in this browser, most recent first, so a shared test laptop can switch quickly.
 * Browser storage can be unavailable (private mode, blocked site data); the widget then asks again.
 */
export function loadReporters(): Reporter[] {
  try {
    const raw = localStorage.getItem(LIST_KEY);
    if (raw)
      return (JSON.parse(raw) as Array<Partial<Reporter>>).filter(valid).slice(0, MAX_REMEMBERED);
    const legacy = localStorage.getItem(LEGACY_KEY);
    const one = legacy ? (JSON.parse(legacy) as Partial<Reporter>) : null;
    return valid(one) ? [{ name: one.name, email: one.email }] : [];
  } catch {
    return [];
  }
}

/** The identity in use: the most recently chosen one. */
export function loadReporter(): Reporter | null {
  return loadReporters()[0] ?? null;
}

function store(list: Reporter[]) {
  try {
    localStorage.setItem(LIST_KEY, JSON.stringify(list.slice(0, MAX_REMEMBERED)));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Not persisted; it still works for this page load.
  }
}

/** Makes this identity the current one and keeps it in the list. */
export function saveReporter(reporter: Reporter): Reporter[] {
  const list = [reporter, ...loadReporters().filter((r) => r.email !== reporter.email)];
  store(list);
  return list.slice(0, MAX_REMEMBERED);
}

/** Removes an identity from this browser only; their feedback stays on the server. */
export function forgetReporter(email: string): Reporter[] {
  const list = loadReporters().filter((r) => r.email !== email);
  store(list);
  return list;
}

/** "sanne.de-vries@ahrend.com" → "Sanne De Vries". A starting point; the tester can change it. */
export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const words = local
    .replace(/\+.*$/, '')
    .split(/[._-]+/)
    .filter((w) => w && !/^\d+$/.test(w));
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
